import { useState } from "react";
import { Box, Divider, Link, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import ContainerWrapper from "../../components/ContainerWrapper/ContainerWrapper";
import { useUserState } from "../../state/user";
import DocumentRow from "../../components/professional/DocumentRow/DocumentRow";
import RemovableEntry from "../../components/professional/RemovableEntry/RemovableEntry";
import ConfirmRemoveDialog from "../../components/professional/ConfirmRemoveDialog/ConfirmRemoveDialog";
import AddEmailDialog from "../../components/professional/AddEmailDialog/AddEmailDialog";
import AddPersonalPhoneDialog from "../../components/professional/AddPersonalPhoneDialog/AddPersonalPhoneDialog";
import {
  IDENTIFYING_DOCUMENT_TYPE,
  currentDocument,
  isCitizenCard
} from "../../types/professional";
import { PersonalPhone, ProfessionalEmail } from "../../services/professionals";
import useProfessionalProfile from "../../hooks/useProfessionalProfile";
import { useTranslation } from "react-i18next";
import { typography } from "../../utils/typography";
import { semanticColors } from "../../utils/colors";
import { getCountry } from "../../utils/countries";

const useStyles = makeStyles(() => ({
  card: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    maxWidth: "600px",
    margin: "24px auto 0"
  },
  row: {
    padding: "16px 24px"
  },
  label: {
    ...typography.body.m,
    color: `${semanticColors.text.secondary} !important`,
    marginBottom: "4px !important"
  },
  value: {
    ...typography.body.lSemibold,
    color: `${semanticColors.text.primary} !important`
  }
}));

/** Puts the add action beside the label of the list it belongs to. */
const headerRowSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2
};

/**
 * `countryPrefix` is an ISO country code, not a dial code, so the dial code is
 * looked up rather than assumed. A country the table does not carry shows the
 * bare number instead of a stray "+".
 */
function formatPhone(phone?: { number?: string; countryPrefix?: string }) {
  if (!phone?.number) return null;
  const dialCode = phone.countryPrefix
    ? getCountry(phone.countryPrefix)?.dialCode
    : undefined;
  return dialCode ? `${dialCode} ${phone.number}` : phone.number;
}

export default function PersonalData() {
  const classes = useStyles();
  const { t } = useTranslation();
  const { user } = useUserState();
  const { removeEmail, removePersonalPhone } = useProfessionalProfile();
  const info = user?.personalInfo ?? {};

  const [addingEmail, setAddingEmail] = useState(false);
  const [addingPhone, setAddingPhone] = useState(false);
  const [removingEmail, setRemovingEmail] = useState<ProfessionalEmail | null>(
    null
  );
  const [removingPhone, setRemovingPhone] = useState<PersonalPhone | null>(
    null
  );

  const gender =
    info.gender === 1
      ? t("utentes.male")
      : info.gender === 2
      ? t("utentes.female")
      : "-";

  /**
   * The address the account was registered with is flagged `main` and stands
   * apart: it identifies the account, and the API refuses to remove it.
   *
   * Entries are tolerated as bare strings too: older records were stored that
   * way, and one without the flag simply reads as an ordinary address. Such an
   * entry has no id, so it cannot be removed either — there is nothing to
   * address the delete to.
   */
  const allEmails: ProfessionalEmail[] = info.emails
    ? Object.values(info.emails).map((entry: any) =>
        typeof entry === "string"
          ? { _id: "", value: entry, main: false }
          : {
              _id: entry?._id ?? "",
              value: entry?.value ?? "",
              personal: entry?.personal,
              main: Boolean(entry?.main)
            }
      )
    : [];

  const mainEmail = allEmails.find(email => email.main)?.value;
  const otherEmails = allEmails.filter(email => !email.main && email.value);

  /** Personal phones, which are their own list — not the practice number. */
  const personalPhones: PersonalPhone[] = info.phoneNumbers
    ? Object.values(info.phoneNumbers).map((entry: any) => ({
        _id: entry?._id ?? "",
        number: entry?.number ?? "",
        countryPrefix: entry?.countryPrefix ?? ""
      }))
    : [];

  /**
   * The citizen card in force. Renewing files a new document rather than
   * editing the old one, so the most recently registered is the current one.
   */
  const citizenCard = currentDocument(info.identifyingDocuments, isCitizenCard);

  // Only the personal phones belong here. The practice and CMD numbers live on
  // the professional data screen, which is also the only place they can be
  // edited — showing them here as well suggested the two were different numbers.
  const rows = [
    { label: t("personalData.name"), value: user?.clinicalName ?? "-" },
    { label: t("personalData.gender"), value: gender },
    { label: t("personalData.fiscalId"), value: info.fiscalId ?? "-" }
  ];

  // The server keeps the last personal phone, so the only one left offers no
  // remove action at all rather than one that always fails.
  const lastPhone = personalPhones.length <= 1;

  /* Each section of the card is named rather than written inline: the card then
     reads as the sequence of sections it is, and each section can be taken in
     on its own. */

  /** One phone is always kept, so the only one left offers no remove action. */
  const personalPhonesSection = (
    <>
      <Divider />
      <div className={classes.row}>
        <Box sx={headerRowSx}>
          <Typography className={classes.label}>
            {t("personalData.personalPhones")}
          </Typography>
          <Link
            component="button"
            type="button"
            onClick={() => setAddingPhone(true)}
          >
            {t("contactLists.add")}
          </Link>
        </Box>
        {personalPhones.length === 0 ? (
          <Typography className={classes.value}>
            {t("contactLists.noPhones")}
          </Typography>
        ) : (
          personalPhones.map((phone, i) => (
            <RemovableEntry
              key={phone._id || i}
              removeLabel={t("contactLists.remove")}
              blockedReason={
                lastPhone ? t("contactLists.blockedLastPhone") : undefined
              }
              onRemove={() => setRemovingPhone(phone)}
            >
              <Typography className={classes.value}>
                {formatPhone(phone) ?? "-"}
              </Typography>
            </RemovableEntry>
          ))
        )}
      </div>
    </>
  );

  /** An entry with no id came from an older record stored as a bare string, and
      cannot be addressed by a delete. */
  const otherEmailsSection = (
    <>
      <Divider />
      <div className={classes.row}>
        <Box sx={headerRowSx}>
          <Typography className={classes.label}>
            {t("personalData.otherEmails")}
          </Typography>
          <Link
            component="button"
            type="button"
            onClick={() => setAddingEmail(true)}
          >
            {t("contactLists.add")}
          </Link>
        </Box>
        {otherEmails.length === 0 ? (
          <Typography className={classes.value}>-</Typography>
        ) : (
          otherEmails.map((email, i) => (
            <RemovableEntry
              key={email._id || i}
              removeLabel={t("contactLists.remove")}
              blockedReason={
                email._id ? undefined : t("contactLists.blockedPrimaryEmail")
              }
              onRemove={() => setRemovingEmail(email)}
            >
              <Typography
                className={classes.value}
                sx={{ wordBreak: "break-word" }}
              >
                {email.value}
              </Typography>
            </RemovableEntry>
          ))
        )}
      </div>
    </>
  );

  return (
    <ContainerWrapper>
      <Typography
        sx={{
          ...typography.heading.l,
          textAlign: "center",
          color: semanticColors.text.primary,
          mb: 0
        }}
      >
        {t("personalData.title")}
      </Typography>

      {/* TODO: still to add via API — Nationality, Place of birth, Date of birth, Citizen Card number */}
      <div className={classes.card}>
        {rows.map((row, i) => (
          <Box key={i}>
            <div className={classes.row}>
              <Typography className={classes.label}>{row.label}</Typography>
              <Typography className={classes.value}>{row.value}</Typography>
            </div>
            {i < rows.length - 1 && <Divider />}
          </Box>
        ))}

        <Divider />
        <div className={classes.row}>
          <DocumentRow
            label={t("identifyingDocuments.citizenCardNumber")}
            document={citizenCard}
            professional={false}
            type={IDENTIFYING_DOCUMENT_TYPE.CITIZEN_CARD}
            viewTitle={t("identifyingDocuments.citizenCard")}
            updateTitle={t("identifyingDocuments.updateCitizenCard")}
            addTitle={t("identifyingDocuments.addCitizenCard")}
          />
        </div>

        {personalPhonesSection}

        {mainEmail && (
          <>
            <Divider />
            <div className={classes.row}>
              <Typography className={classes.label}>
                {t("personalData.mainEmail")}
              </Typography>
              <Typography className={classes.value}>{mainEmail}</Typography>
            </div>
          </>
        )}

        {otherEmailsSection}
      </div>

      <AddEmailDialog
        open={addingEmail}
        existing={allEmails.map(email => email.value)}
        onClose={() => setAddingEmail(false)}
        onAdded={() => setAddingEmail(false)}
      />

      <AddPersonalPhoneDialog
        open={addingPhone}
        existing={personalPhones}
        onClose={() => setAddingPhone(false)}
        onAdded={() => setAddingPhone(false)}
      />

      <ConfirmRemoveDialog
        open={removingEmail !== null}
        title={t("contactLists.removeEmailTitle")}
        subject={removingEmail?.value ?? ""}
        onClose={() => setRemovingEmail(null)}
        onConfirm={async () => {
          if (!removingEmail?._id) return;
          await removeEmail(removingEmail._id);
        }}
      />

      <ConfirmRemoveDialog
        open={removingPhone !== null}
        title={t("contactLists.removePhoneTitle")}
        subject={formatPhone(removingPhone ?? undefined) ?? ""}
        onClose={() => setRemovingPhone(null)}
        onConfirm={async () => {
          if (!removingPhone?._id) return;
          await removePersonalPhone(removingPhone._id);
        }}
      />
    </ContainerWrapper>
  );
}
